/**
 * Which installed PWA is running, if any.
 *
 * Each per-area manifest's `start_url` carries `?app=<slug>`. That marker is the
 * ONLY signal — the landing pathname is deliberately not used, because the root
 * Conway app is scoped to `/` and lands on `/attention`, which is exactly the
 * Inbox app's start path. Any pathname inference strong enough to be useful
 * would misidentify plain Conway as the Inbox app.
 *
 * Getting this wrong renders the wrong app's chrome and never raises an error,
 * so all three guards here are load-bearing: standalone-only, init-only capture,
 * and stripping the marker the moment it is read.
 */

/** The installable areas. Must match the `?app=` values in the manifests. */
export const APP_SLUGS = ['attention', 'tasks', 'food', 'chat', 'study'] as const;

export type AppSlug = (typeof APP_SLUGS)[number];

const STORAGE_KEY = 'conway-installed-app';
const MARKER_PARAM = 'app';

const isAppSlug = (value: string | null): value is AppSlug =>
  value !== null && (APP_SLUGS as readonly string[]).includes(value);

/** True when the page is running as an installed app rather than a browser tab. */
const isStandalone = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(display-mode: standalone)').matches;

/** Persist or clear the slug. sessionStorage throws in some privacy modes, and app identity is never worth a blank screen. */
function writeSlug(slug: AppSlug | null): void {
  try {
    if (slug === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, slug);
  } catch {
    // Storage unavailable — degrade to the full nav rather than failing init.
  }
}

let captured = false;

/** The slug this page load was launched as, until `takeLaunch` reads it. */
let launched: AppSlug | null = null;

/**
 * Read the launch marker once and remember it for this browsing context.
 *
 * Call exactly once, at application init, BEFORE routing — an in-app navigation
 * drops the query string, so a later read would find nothing.
 *
 * `sessionStorage`, not `localStorage`: Chrome shares localStorage between an
 * installed app and ordinary browser tabs on the same origin, so a slug written
 * there by one app would select the wrong chrome everywhere else. A session is
 * scoped to the browsing context on every platform, and nothing is lost by the
 * shorter lifetime because `start_url` re-supplies the marker on every launch.
 */
export function captureInstalledApp(): void {
  if (captured) return;
  captured = true;
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const marker = url.searchParams.get(MARKER_PARAM);

  // No marker means a reload or an in-app deep link. Leave any stored slug
  // alone: a reload inside Conway Tasks is still Conway Tasks. A genuinely cold
  // launch is a NEW browsing context, whose session jar is already empty.
  if (marker === null) return;

  // Strip it whatever it turns out to be, so it can never be bookmarked,
  // shared, or re-read into the root app's session. Preserve the existing
  // history state — replacing it with {} would discard router metadata.
  url.searchParams.delete(MARKER_PARAM);
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  );

  // A marker that is present is authoritative for this launch: an unrecognised
  // one CLEARS any stored value rather than leaving stale branding in place.
  // Honoured only in standalone — a browser tab keeps the full nav.
  const slug = isStandalone() && isAppSlug(marker) ? marker : null;
  writeSlug(slug);
  // A marked launch is a fresh intent: the caller open-or-focuses the app's
  // start tab (nav spec §4.10). A reload or deep link carries no marker.
  launched = slug;
}

/**
 * The app this page load launched as, exactly once; null afterwards, on a
 * reload, on a deep link, and in a browser tab. Read-and-clear so the launch
 * acts once even if two callers ask.
 */
export function takeLaunch(): AppSlug | null {
  const slug = launched;
  launched = null;
  return slug;
}

/**
 * The running installed app, or null in a browser tab and in the root Conway app.
 *
 * Re-checks standalone on every read rather than trusting the stored value:
 * sessionStorage is per browsing context, but Chrome COPIES it into a duplicated
 * tab — and a duplicate is a browser tab, which must get the full nav.
 */
export function getInstalledApp(): AppSlug | null {
  if (typeof window === 'undefined') return null;
  if (!isStandalone()) return null;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return isAppSlug(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Test-only: allow re-capture, simulating a fresh page load. */
export function __resetInstalledAppForTests(): void {
  captured = false;
  launched = null;
}
